/**
 * OSP API response helpers: unwrap swagger client promises and raw fetch responses.
 *
 * @source @open-social-protocol/osp-api-index-types/src/result-wrapper.ts
 */

import { IndexError } from "./errors";
import type { OspApiBaseResponse, OspApiHttpResponse } from "./types";

export async function wrapperResult<R>(
  result: Promise<
    OspApiHttpResponse<OspApiBaseResponse & { data?: R & object }, unknown>
  >,
): Promise<R> {
  return result
    .then((r) => {
      if (r.data.code !== 0) {
        throw new IndexError(r.data);
      }
      return r.data.data as R;
    })
    .catch((e: unknown) => {
      if (e instanceof IndexError) {
        throw e;
      }
      const err = e as { error?: unknown };
      throw new IndexError(err.error);
    });
}

/** Unwraps a bare `fetch` Response using the same `{ code, data }` envelope as swagger clients. */
export async function unwrapOspFetchResponse<T extends object>(
  response: Response,
): Promise<T> {
  const body = (await response.json()) as OspApiBaseResponse & { data?: T };

  if (!response.ok) {
    throw new IndexError(body);
  }

  return wrapperResult(
    Promise.resolve({
      ok: response.ok,
      data: body,
    } as OspApiHttpResponse<OspApiBaseResponse & { data?: T }, unknown>),
  );
}
